<template>
  <transition name="birthday-modal">
    <div v-if="isActive"
         aria-describedby="birthday-modal-description"
         aria-labelledby="birthday-modal-title"
         aria-modal="true"
         class="birthday-modal"
         role="dialog"
         @click.self="closeModal"
    >
      <section class="birthday-modal__panel">
        <button
            ref="closeButton"
            :aria-label="$t('general.close')"
            class="birthday-modal__close"
            type="button"
            @click="closeModal"
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <div aria-hidden="true" class="birthday-modal__visual">
          <span class="confetti confetti--one"></span>
          <span class="confetti confetti--two"></span>
          <span class="confetti confetti--three"></span>
          <span class="confetti confetti--four"></span>
          <span class="confetti confetti--five"></span>
          <span class="confetti confetti--six"></span>

          <div class="birthday-modal__halo"></div>
          <div class="birthday-modal__cake">
            <span class="birthday-modal__flame"></span>
            <span class="birthday-modal__candle"></span>
            <span class="birthday-modal__icing"></span>
            <span class="birthday-modal__cake-body"></span>
            <span class="birthday-modal__plate"></span>
          </div>

          <p class="birthday-modal__wish">
            {{ $t("general.birthday_celebration_title") }}
          </p>
        </div>

        <div class="birthday-modal__content">
          <span class="birthday-modal__eyebrow">ANZERA</span>
          <h2 id="birthday-modal-title" class="birthday-modal__title">
            {{ $t("general.birthday_celebration_title") }}!
          </h2>
          <p class="birthday-modal__greeting">
            {{ $t("general.hi") }} <strong>{{ userName }}</strong
          >,
          </p>
          <p
              id="birthday-modal-description"
              class="birthday-modal__description"
          >
            {{ $t("general.birthday_celebration_description") }} 😊
          </p>

          <div class="birthday-modal__divider">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <button class="birthday-modal__action"
                  type="button"
                  @click="closeModal"
          >
            <span>{{ $t("general.close") }}</span>
            <span aria-hidden="true" class="birthday-modal__action-icon"
            >&#10024;</span
            >
          </button>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
import SecureLS from "secure-ls";
import moment from "moment";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });

export default {
  name: "BirthdayModal",
  data() {
    return {
      isActive: false,
      previousBodyOverflow: "",
      bodyLocked: false,
    };
  },
  computed: {
    userName() {
      return this.authUser && this.authUser.fullname
          ? this.authUser.fullname
          : this.$t("general.user");
    },
  },
  mounted() {
    this.birthDayModal();

    if (!this.isActive) {
      return;
    }

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.bodyLocked = true;
    document.addEventListener("keydown", this.handleKeydown);

    this.$nextTick(() => {
      if (this.$refs.closeButton) {
        this.$refs.closeButton.focus();
      }
    });
  },
  beforeDestroy() {
    this.releaseModal();
  },
  methods: {
    birthDayModal() {
      // Keep the original behavior: show once, only on the user's birthday.
      if (!this.authUser || !this.authUser.dob || ls.get("userBirthday")) {
        return;
      }

      const today = new Date();
      const inputDate = new Date(this.authUser.dob);
      const isBirthday =
          !Number.isNaN(inputDate.getTime()) &&
          moment(inputDate).format("DD MMMM") === moment(today).format("DD MMMM");

      if (isBirthday) {
        this.isActive = true;
        ls.set("userBirthday", true);
      }
    },
    closeModal() {
      this.isActive = false;
      this.releaseModal();
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        this.closeModal();
      }
    },
    releaseModal() {
      if (!this.bodyLocked) {
        return;
      }
      document.body.style.overflow = this.previousBodyOverflow;
      this.bodyLocked = false;
      document.removeEventListener("keydown", this.handleKeydown);
    },
  },
};
</script>

<style scoped>
.birthday-modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
  background: radial-gradient(
      circle at 15% 15%,
      rgba(255, 188, 117, 0.18),
      transparent 28%
  ),
  radial-gradient(circle at 85% 85%, rgba(128, 90, 213, 0.2), transparent 30%),
  rgba(23, 16, 43, 0.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.birthday-modal__panel {
  position: relative;
  display: grid;
  grid-template-columns: minmax(300px, 0.9fr) minmax(340px, 1.1fr);
  width: min(860px, 100%);
  min-height: 500px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 30px;
  box-shadow: 0 34px 90px rgba(17, 10, 35, 0.38);
}

.birthday-modal__close {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 4;
  display: grid;
  width: 42px;
  height: 42px;
  padding: 0;
  color: #594c72;
  font-size: 28px;
  line-height: 1;
  place-items: center;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(91, 69, 132, 0.12);
  border-radius: 50%;
  box-shadow: 0 8px 24px rgba(55, 37, 86, 0.12);
  cursor: pointer;
  transition: transform 180ms ease, background-color 180ms ease,
  color 180ms ease;
}

.birthday-modal__close:hover,
.birthday-modal__close:focus-visible {
  color: #6d3dc0;
  background: #ffffff;
  outline: none;
  transform: rotate(8deg) scale(1.06);
}

.birthday-modal__visual {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 500px;
  overflow: hidden;
  background: linear-gradient(145deg, #6f45bb 0%, #925bd1 52%, #f08a9d 130%);
}

.birthday-modal__visual::before,
.birthday-modal__visual::after {
  position: absolute;
  width: 260px;
  height: 260px;
  content: "";
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 50%;
}

.birthday-modal__visual::before {
  top: -128px;
  left: -104px;
  box-shadow: 0 0 0 38px rgba(255, 255, 255, 0.04);
}

.birthday-modal__visual::after {
  right: -150px;
  bottom: -100px;
  box-shadow: 0 0 0 46px rgba(255, 255, 255, 0.04);
}

.birthday-modal__halo {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 290px;
  height: 290px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  box-shadow: 0 0 70px rgba(255, 211, 155, 0.18);
  transform: translate(-50%, -55%);
}

.birthday-modal__cake {
  position: relative;
  width: 230px;
  height: 220px;
  filter: drop-shadow(0 25px 24px rgba(44, 18, 77, 0.24));
  animation: cake-float 3.4s ease-in-out infinite;
}

.birthday-modal__plate,
.birthday-modal__cake-body,
.birthday-modal__icing,
.birthday-modal__candle,
.birthday-modal__flame {
  position: absolute;
  left: 50%;
  display: block;
  transform: translateX(-50%);
}

.birthday-modal__plate {
  bottom: 10px;
  width: 218px;
  height: 20px;
  background: linear-gradient(180deg, #ffffff, #ddd4f0);
  border-radius: 50%;
}

.birthday-modal__cake-body {
  bottom: 23px;
  width: 174px;
  height: 100px;
  background: repeating-linear-gradient(
      90deg,
      #f2a0ad 0 22px,
      #f7b1ba 22px 44px
  );
  border-radius: 10px 10px 22px 22px;
  box-shadow: inset 0 -14px 0 rgba(155, 59, 92, 0.1);
}

.birthday-modal__icing {
  bottom: 105px;
  z-index: 2;
  width: 184px;
  height: 38px;
  background: #fff7ee;
  border-radius: 50% 50% 35% 35%;
  box-shadow: inset 0 -8px 0 #f3e0d5;
}

.birthday-modal__icing::after {
  position: absolute;
  top: 23px;
  left: 18px;
  width: 25px;
  height: 26px;
  content: "";
  background: #f3e0d5;
  border-radius: 0 0 15px 15px;
  box-shadow: 54px -4px 0 #f3e0d5, 108px 2px 0 #f3e0d5;
}

.birthday-modal__candle {
  bottom: 137px;
  width: 16px;
  height: 43px;
  background: repeating-linear-gradient(
      135deg,
      #ffd46a 0 7px,
      #ffffff 7px 13px
  );
  border-radius: 5px 5px 2px 2px;
}

.birthday-modal__flame {
  bottom: 185px;
  width: 22px;
  height: 30px;
  background: linear-gradient(135deg, #fff4a3, #ff9b42 70%);
  border-radius: 70% 35% 65% 35%;
  box-shadow: 0 0 20px rgba(255, 197, 80, 0.8);
  transform: translateX(-50%) rotate(45deg);
  animation: flame-flicker 1.4s ease-in-out infinite alternate;
}

.birthday-modal__wish {
  position: relative;
  z-index: 2;
  margin: 12px 0 0;
  color: rgba(255, 255, 255, 0.92);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.confetti {
  position: absolute;
  z-index: 1;
  width: 9px;
  height: 20px;
  border-radius: 3px;
  transform: rotate(25deg);
  animation: confetti-drift 4s ease-in-out infinite;
}

.confetti--one {
  top: 17%;
  left: 16%;
  background: #ffd46a;
}

.confetti--two {
  top: 25%;
  right: 17%;
  width: 12px;
  height: 12px;
  background: #8de3d1;
  border-radius: 50%;
  animation-delay: -0.8s;
}

.confetti--three {
  bottom: 21%;
  left: 12%;
  background: #ffabc0;
  animation-delay: -1.6s;
}

.confetti--four {
  right: 12%;
  bottom: 25%;
  background: #ffd46a;
  animation-delay: -2.4s;
}

.confetti--five {
  top: 10%;
  left: 48%;
  width: 12px;
  height: 12px;
  background: #ffffff;
  border-radius: 50%;
  animation-delay: -1.2s;
}

.confetti--six {
  right: 29%;
  bottom: 10%;
  background: #8de3d1;
  animation-delay: -3s;
}

.birthday-modal__content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 70px 64px 60px;
  text-align: left;
  background: radial-gradient(
      circle at 100% 0,
      rgba(244, 218, 255, 0.55),
      transparent 30%
  ),
  #ffffff;
}

.birthday-modal__eyebrow {
  align-self: flex-start;
  margin-bottom: 18px;
  padding: 7px 12px;
  color: #7144b5;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.18em;
  background: #f2eafd;
  border-radius: 999px;
}

.birthday-modal__title {
  max-width: 390px;
  margin: 0 0 22px;
  color: #24163c;
  font-size: clamp(34px, 4vw, 50px);
  font-weight: 800;
  line-height: 1.06;
  letter-spacing: -0.04em;
}

.birthday-modal__greeting {
  margin: 0 0 10px;
  color: #46375d;
  font-size: 18px;
}

.birthday-modal__greeting strong {
  color: #6f45bb;
  font-weight: 800;
}

.birthday-modal__description {
  max-width: 410px;
  margin: 0;
  color: #756985;
  font-size: 16px;
  line-height: 1.75;
}

.birthday-modal__divider {
  display: flex;
  gap: 7px;
  margin: 27px 0;
}

.birthday-modal__divider span {
  display: block;
  width: 8px;
  height: 8px;
  background: #8e59cc;
  border-radius: 50%;
}

.birthday-modal__divider span:nth-child(2) {
  background: #ef8ca2;
}

.birthday-modal__divider span:nth-child(3) {
  background: #f3bd56;
}

.birthday-modal__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  min-width: 156px;
  min-height: 48px;
  padding: 12px 22px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  background: linear-gradient(135deg, #7144b5, #9356c8);
  border: 0;
  border-radius: 14px;
  box-shadow: 0 12px 25px rgba(113, 68, 181, 0.25);
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.birthday-modal__action:hover,
.birthday-modal__action:focus-visible {
  color: #ffffff;
  outline: none;
  box-shadow: 0 16px 30px rgba(113, 68, 181, 0.34);
  transform: translateY(-2px);
}

.birthday-modal__action-icon {
  margin-left: 10px;
  font-size: 16px;
}

.birthday-modal-enter-active,
.birthday-modal-leave-active {
  transition: opacity 260ms ease;
}

.birthday-modal-enter-active .birthday-modal__panel,
.birthday-modal-leave-active .birthday-modal__panel {
  transition: transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 260ms ease;
}

.birthday-modal-enter,
.birthday-modal-leave-to {
  opacity: 0;
}

.birthday-modal-enter .birthday-modal__panel,
.birthday-modal-leave-to .birthday-modal__panel {
  opacity: 0;
  transform: translateY(18px) scale(0.97);
}

@keyframes cake-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

@keyframes flame-flicker {
  from {
    transform: translateX(-50%) rotate(42deg) scale(0.96);
  }
  to {
    transform: translateX(-50%) rotate(48deg) scale(1.06);
  }
}

@keyframes confetti-drift {
  0%,
  100% {
    transform: translateY(0) rotate(25deg);
  }
  50% {
    transform: translateY(12px) rotate(45deg);
  }
}

@media (max-width: 767px) {
  .birthday-modal {
    align-items: flex-start;
    padding: 16px;
  }

  .birthday-modal__panel {
    grid-template-columns: 1fr;
    width: min(440px, 100%);
    min-height: 0;
    margin: auto 0;
    border-radius: 24px;
  }

  .birthday-modal__visual {
    min-height: 245px;
  }

  .birthday-modal__halo {
    width: 220px;
    height: 220px;
  }

  .birthday-modal__cake {
    width: 210px;
    height: 190px;
    margin-top: 12px;
  }

  .birthday-modal__wish {
    margin-top: -8px;
    font-size: 12px;
  }

  .birthday-modal__content {
    padding: 34px 28px 32px;
    text-align: center;
  }

  .birthday-modal__eyebrow,
  .birthday-modal__action {
    align-self: center;
  }

  .birthday-modal__title {
    margin-bottom: 16px;
    font-size: 34px;
  }

  .birthday-modal__description {
    line-height: 1.6;
  }

  .birthday-modal__divider {
    justify-content: center;
    margin: 20px 0;
  }
}

@media (max-height: 680px) and (min-width: 768px) {
  .birthday-modal {
    align-items: flex-start;
  }

  .birthday-modal__panel {
    min-height: 460px;
    margin: auto 0;
  }

  .birthday-modal__visual {
    min-height: 460px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .birthday-modal *,
  .birthday-modal-enter-active,
  .birthday-modal-leave-active {
    animation: none !important;
    transition: none !important;
  }
}
</style>
